import { create } from "zustand";
import { devtools } from "zustand/middleware";
import APIs from "../application/APIs";
import {
  GeneratedDocumentResponse,
  CollectionFieldInfo,
} from "../types/generatedTypes";

// Re-export for consumers
export type { CollectionFieldInfo };

// Local: API returns `identifier` but generated TemplateClientResponse has `id` — keep local until type generation is fixed
export interface Client {
  identifier: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  city?: string;
  country?: string;
}

// Local: bulk generation result shape, not a direct API response type
export interface GeneratedDocument {
  client_id: string;
  client_name: string;
  template_name: string;
  field_values: Record<string, string>;
  related_data?: ClientRelatedData;
  status: string;
}

// Local: typed shape of the related_data object returned by the API
export interface ClientRelatedData {
  client: Record<string, unknown>;
  companies: Record<string, unknown>[];
  bank_accounts: Record<string, unknown>[];
  files: Record<string, unknown>[];
  funds: Record<string, unknown>[];
  contacts: Record<string, unknown>[];
}

// API returns 'identifier' instead of 'id', plus field_values not in generated type
export interface SavedGeneratedDocument extends Omit<GeneratedDocumentResponse, "id"> {
  identifier: string;
  field_values: Record<string, unknown>;
}

interface FlowsSavedDocumentsState {
  // Clients
  clients: Client[];
  clientsLoading: boolean;
  selectedClientIds: string[];
  selectedTemplateId: string | null;

  // Generated Documents (bulk)
  generatedDocuments: GeneratedDocument[];
  generatingDocuments: boolean;

  // Collections
  availableCollections: CollectionFieldInfo[];
  collectionsLoading: boolean;
  selectedClientRelatedData: Record<string, ClientRelatedData>;

  // Saved Generated Documents
  savedGeneratedDocuments: SavedGeneratedDocument[];
  savedGeneratedDocumentsLoading: boolean;

  error: string | null;

  // Template Selection
  setSelectedTemplate: (templateId: string | null) => void;

  // Clients Actions
  fetchClients: () => Promise<void>;
  setSelectedClientIds: (clientIds: string[]) => void;
  toggleClientSelection: (clientId: string) => void;
  selectAllClients: () => void;
  clearClientSelection: () => void;
  generateBulkDocuments: () => Promise<void>;
  clearGeneratedDocuments: () => void;

  // Collections Actions
  fetchAvailableCollections: () => Promise<void>;
  fetchClientRelatedData: (clientId: string) => Promise<ClientRelatedData | null>;

  // Saved Generated Documents Actions
  fetchSavedGeneratedDocuments: () => Promise<void>;
  saveGeneratedDocument: (data: {
    template_id: string;
    name: string;
    client_id?: string;
    client_name?: string;
    field_values: Record<string, unknown>;
    placeholders?: Array<{
      id: string;
      page: number;
      db_column: string;
      rect: { x: number; y: number; w: number; h: number };
      style: { font_name: string; font_size: number; color: string; align?: string };
      currentValue?: string;
    }>;
  }) => Promise<SavedGeneratedDocument | null>;
  downloadSavedGeneratedDocument: (documentId: string) => Promise<void>;
  deleteSavedGeneratedDocument: (documentId: string) => Promise<void>;

  clearError: () => void;
}

const useFlowsSavedDocumentsStore = create<FlowsSavedDocumentsState>()(
  devtools(
    (set, get) => ({
      clients: [],
      clientsLoading: false,
      selectedClientIds: [],
      selectedTemplateId: null,
      generatedDocuments: [],
      generatingDocuments: false,
      availableCollections: [],
      collectionsLoading: false,
      selectedClientRelatedData: {},
      savedGeneratedDocuments: [],
      savedGeneratedDocumentsLoading: false,
      error: null,

      setSelectedTemplate: (templateId) => set({ selectedTemplateId: templateId }),

      fetchClients: async () => {
        set({ clientsLoading: true, error: null });
        try {
          const response = await APIs.getFlowsAutomationsClients();
          set({ clients: response.clients, clientsLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to fetch clients:", error);
          set({ error: `Client fetch failed: ${message}`, clientsLoading: false });
        }
      },

      setSelectedClientIds: (clientIds) => set({ selectedClientIds: clientIds }),

      toggleClientSelection: (clientId) => {
        set((state) => {
          const isSelected = state.selectedClientIds.includes(clientId);
          return {
            selectedClientIds: isSelected
              ? state.selectedClientIds.filter((id) => id !== clientId)
              : [...state.selectedClientIds, clientId],
          };
        });
      },

      selectAllClients: () => {
        set((state) => ({
          selectedClientIds: state.clients.map((c) => c.identifier),
        }));
      },

      clearClientSelection: () => set({ selectedClientIds: [] }),

      generateBulkDocuments: async () => {
        const { selectedTemplateId, selectedClientIds } = get();
        if (!selectedTemplateId || selectedClientIds.length === 0) return;

        set({ generatingDocuments: true, error: null });
        try {
          const response = await APIs.generateBulkDocuments(selectedTemplateId, selectedClientIds);
          if (response.data) {
            set({
              generatedDocuments: response.data.documents,
              generatingDocuments: false,
            });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to generate documents:", error);
          set({ error: `Document generation failed: ${message}`, generatingDocuments: false });
        }
      },

      clearGeneratedDocuments: () => set({ generatedDocuments: [] }),

      fetchAvailableCollections: async () => {
        set({ collectionsLoading: true, error: null });
        try {
          const response = await APIs.getAvailableCollections();
          set({ availableCollections: response.collections, collectionsLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to fetch collections:", error);
          set({ error: `Collections fetch failed: ${message}`, collectionsLoading: false });
        }
      },

      fetchClientRelatedData: async (clientId: string) => {
        try {
          const response = await APIs.getClientRelatedData(clientId);
          set((state) => ({
            ...state,
            selectedClientRelatedData: {
              ...state.selectedClientRelatedData,
              [clientId]: response.related_data as ClientRelatedData,
            },
          }));
          return response.related_data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error(`Failed to fetch related data for client ${clientId}:`, error);
          set({ error: `Client related data fetch failed for ${clientId}: ${message}` });
          return null;
        }
      },

      fetchSavedGeneratedDocuments: async () => {
        set({ savedGeneratedDocumentsLoading: true, error: null });
        try {
          const response = await APIs.getSavedGeneratedDocuments();
          set({ savedGeneratedDocuments: response.documents as unknown as SavedGeneratedDocument[], savedGeneratedDocumentsLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to fetch saved generated documents:", error);
          set({ error: `Saved documents fetch failed: ${message}`, savedGeneratedDocumentsLoading: false });
        }
      },

      saveGeneratedDocument: async (data) => {
        set({ error: null });
        try {
          const response = await APIs.saveGeneratedDocument(data);
          const newDoc = response.document;
          set((state) => ({
            ...state,
            savedGeneratedDocuments: [newDoc as unknown as SavedGeneratedDocument, ...state.savedGeneratedDocuments],
          }));
          return newDoc;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to save generated document:", error);
          set({ error: `Document save failed: ${message}` });
          return null;
        }
      },

      downloadSavedGeneratedDocument: async (documentId: string) => {
        try {
          const blob = await APIs.downloadGeneratedDocument(documentId);
          const doc = get().savedGeneratedDocuments.find((d) => d.identifier === documentId);
          const filename = doc?.file_name || "document.pdf";

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to download document:", error);
          set({ error: `Document download failed: ${message}` });
        }
      },

      deleteSavedGeneratedDocument: async (documentId: string) => {
        try {
          await APIs.deleteGeneratedDocument(documentId);
          set((state) => ({
            savedGeneratedDocuments: state.savedGeneratedDocuments.filter((d) => d.identifier !== documentId),
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to delete document:", error);
          set({ error: `Document deletion failed: ${message}` });
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: "flows-saved-documents-store" }
  )
);

export const useFlowsSavedDocuments = () => useFlowsSavedDocumentsStore();

export default useFlowsSavedDocumentsStore;
